// @flow
import Vue from 'vue'
import { getCookie } from 'tiny-cookie'

import Logger from '@/logger'
import settings from '@/settings'
import { toLocaleISOCode, getFallbackLocale } from './helpers'

export const defaultLanguage = String(process.env.VUE_APP_DEFAULT_LOCALE)
export const cookieLocaleKey = String(process.env.VUE_APP_LOCALE_KEY)

/**
 * Async load translation json
 * @param {String} lang
 * @returns {Promise} Localization messages
 */
export async function loadLanguage (lang) {
  const isoLocaleCode = toLocaleISOCode(lang)
  const fallbackLocale = getFallbackLocale(isoLocaleCode)
  let messages = {}
  try {
    messages = await import(
      /* webpackInclude: /\.json$/ */
      /* webpackChunkName: "locale/[request]" */
      `../../locale/${isoLocaleCode}`
    )
  } catch (e) {
    Logger.error(`Failed to load translation "${isoLocaleCode}"`)
    if (fallbackLocale) {
      Logger.debug(`Loadind fallback locale "${fallbackLocale}`)
      return loadLanguage(fallbackLocale)
    }
  }
  return messages.default && Object.values(messages.default)[0]
}

/**
 * Mixin for `vue-gettext`
 * Provided via `$language` property
 */
export const languageVmMixin = {
  methods: {
    /**
     * Get User selected locale (or default locale)
     * @return {String} locale
     *
     * Usage:
     *  vm.$language.getUserLanguage()
     */
    getUserLanguage () {
      const cookieLanguage = getCookie(cookieLocaleKey)
      return (
        cookieLanguage ||
        settings.get('LANGUAGE') ||
        settings.get('DEFAULT_LANGUAGE') ||
        defaultLanguage
      )
    },

    /**
     * Async load localization messages and change locale.
     * @param {String} lang
     *
     * Usage:
     *  await vm.$language.setLanguage('en')
     */
    async setLanguage (lang) {
      if (!this.available[lang]) {
        throw new Error(`Language "${lang}" is not available.`)
      }

      if (!Vue.$translations[lang]) {
        const messages = await loadLanguage(lang)
        Vue.$translations[lang] = messages || {} // assumed message file in format {'<lang>': { ... }}
      }

      this.current = lang
    }
  }
}
